import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { motion, useReducedMotion } from "framer-motion";
import { Phone, MapPin, Gift } from "lucide-react";
import Logo from "../components/site/Logo";
import Reveal from "../components/site/Reveal";
import CallbackForm from "../components/site/CallbackForm";
import { CONTACT, PORTRAIT } from "../lib/site";
import { trackEvent } from "../lib/track";

const EASE = [0.22, 1, 0.36, 1];
const OFFER_ID = "sonicare";
const OFFER_LABEL = "New Patient Experience (Sonicare offer)";

const INCLUDED = [
  {
    name: "Comprehensive dental examination",
    body:
      "A full assessment of your teeth, gums, bite and jaw, including a routine oral cancer screening. We review your dental and medical history and talk through any concerns before anything begins.",
  },
  {
    name: "Diagnostic digital X-rays",
    body:
      "Low radiation digital imaging that shows what an examination alone cannot, including decay between teeth and the health of the bone supporting them.",
  },
  {
    name: "iTero 3D digital scan",
    body:
      "A quick, comfortable digital scan of your mouth with no impression trays. The 3D model lets us show you exactly what we see and plan any treatment precisely.",
  },
  {
    name: "Periodontal assessment",
    body:
      "A detailed evaluation of your gum health, measuring the tissue around each tooth to identify early signs of gum disease.",
  },
  {
    name: "60-minute dental cleaning & polish",
    body:
      "A professional cleaning with a Registered Dental Hygienist to remove plaque and tartar, finished with a polish.",
  },
];

export default function NewPatientExperience() {
  const reduce = useReducedMotion();

  // Ads-only page. Kept out of search so it never competes with the main site.
  useEffect(() => {
    document.title =
      "New Patient Experience | Alae Dental, Vaughan";
    const metas = [
      ["robots", "noindex, nofollow"],
      [
        "description",
        "New Patient Experience at Alae Dental in Vaughan. A comprehensive exam, digital X-rays, iTero 3D scan, periodontal assessment and a 60-minute cleaning, with a Philips Sonicare electric toothbrush to take home.",
      ],
    ];
    const created = [];
    metas.forEach(([name, content]) => {
      let el = document.head.querySelector(`meta[name="${name}"]`);
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute("name", name);
        document.head.appendChild(el);
        created.push(el);
      }
      el.setAttribute("content", content);
    });
    const canonical = document.head.querySelector('link[rel="canonical"]');
    if (canonical) canonical.remove();
    return () => {
      const robots = document.head.querySelector('meta[name="robots"]');
      if (robots) robots.setAttribute("content", "index, follow");
      created.forEach((el) => el.remove());
    };
  }, []);

  return (
    <main data-testid="new-patient-page" className="bg-linen text-umber">
      {/* Minimal header. No site nav, no competing promotions. */}
      <header className="sticky top-0 z-40 bg-paper/90 backdrop-blur-md border-b border-umber/10">
        <div className="max-w-6xl mx-auto px-6 md:px-10 h-16 md:h-20 flex items-center justify-between gap-4">
          <Link to="/" data-testid="np-logo" aria-label="Alae Dental home">
            <Logo size="sm" className="h-9 md:h-11" />
          </Link>
          <div className="flex items-center gap-2 md:gap-4">
            <a
              href={`tel:${CONTACT.phoneTel}`}
              data-testid="np-call"
              onClick={() => trackEvent("call_click", { offer: OFFER_ID, placement: "np-header" })}
              className="inline-flex items-center gap-2 text-[11px] md:text-[12px] tracking-[0.18em] uppercase text-umber hover:text-forest transition-colors px-3 py-3 min-h-[44px]"
            >
              <Phone size={15} strokeWidth={1.7} />
              <span className="hidden sm:inline">{CONTACT.phoneLabel}</span>
              <span className="sm:hidden">Call</span>
            </a>
            <a
              href="#callback"
              data-testid="np-request-callback-header"
              className="bg-forest text-paper rounded-full px-5 py-3 text-[11px] tracking-[0.18em] uppercase hover:bg-sage transition-colors min-h-[44px] inline-flex items-center"
            >
              Request a Callback
            </a>
          </div>
        </div>
      </header>

      {/* 1. The offer */}
      <section className="bg-paper paper-grain border-b border-umber/10">
        <div className="max-w-6xl mx-auto px-6 md:px-10 py-14 md:py-24 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
          <motion.div
            className="lg:col-span-7"
            initial={reduce ? false : { opacity: 0, y: 24 }}
            animate={reduce ? undefined : { opacity: 1, y: 0 }}
            transition={{ duration: 0.9, ease: EASE }}
          >
            <div className="flex items-center gap-3 mb-6">
              <span className="h-[1px] w-10 bg-forest" />
              <span className="font-meta text-forest">Now Open in Vaughan</span>
            </div>

            <h1 className="font-display text-4xl md:text-5xl lg:text-6xl leading-[1.1] tracking-tight text-umber">
              The New Patient{" "}
              <span className="font-display-italic text-forest">Experience.</span>
            </h1>

            <p className="mt-7 text-base md:text-lg leading-relaxed text-umber/85 max-w-xl">
              A thorough, unhurried first visit with Dr. Nausheen Mirza, built to
              give you a complete picture of your oral health. New patients also
              receive a Philips Sonicare electric toothbrush to take home.
            </p>

            <div
              data-testid="np-gift"
              className="mt-8 inline-flex items-start gap-4 border border-forest/25 bg-linen/70 px-6 py-5 max-w-xl"
            >
              <Gift size={20} className="text-forest mt-0.5 shrink-0" strokeWidth={1.6} />
              <p className="text-sm leading-relaxed text-umber/85">
                A complimentary{" "}
                <strong className="font-normal text-umber">
                  Philips Sonicare electric toothbrush
                </strong>{" "}
                for every new patient who completes their New Patient Experience.
              </p>
            </div>

            <div className="mt-9 flex flex-col sm:flex-row gap-4">
              <a
                href="#callback"
                data-testid="np-request-callback"
                className="bg-forest text-paper rounded-full px-9 py-4 text-[12px] tracking-[0.22em] uppercase hover:bg-sage transition-colors duration-300 text-center min-h-[44px] inline-flex items-center justify-center"
              >
                Request a Callback
              </a>
              <a
                href={`tel:${CONTACT.phoneTel}`}
                data-testid="np-call-hero"
                onClick={() => trackEvent("call_click", { offer: OFFER_ID, placement: "np-hero" })}
                className="border border-umber/35 text-umber rounded-full px-9 py-4 text-[12px] tracking-[0.22em] uppercase hover:border-forest hover:text-forest transition-colors duration-300 text-center min-h-[44px] inline-flex items-center justify-center gap-2"
              >
                <Phone size={15} strokeWidth={1.7} />
                Call {CONTACT.phoneLabel}
              </a>
            </div>
          </motion.div>

          <motion.div
            className="lg:col-span-5"
            initial={reduce ? false : { opacity: 0, scale: 1.03 }}
            animate={reduce ? undefined : { opacity: 1, scale: 1 }}
            transition={{ duration: 1.1, ease: EASE, delay: 0.1 }}
          >
            <div className="relative">
              <img
                src={PORTRAIT.drMirza}
                alt={PORTRAIT.drMirzaAlt}
                width="720"
                height="900"
                fetchPriority="high"
                decoding="sync"
                className="w-full h-auto object-cover shadow-paper"
              />
              <div className="absolute -bottom-4 left-4 right-4 bg-paper/95 backdrop-blur-sm border border-umber/15 px-5 py-3">
                <div className="font-display text-lg text-umber leading-snug">
                  Dr. Nausheen Mirza
                </div>
                <div className="text-[11px] tracking-[0.16em] uppercase text-umber/60 mt-1">
                  General Dentist &amp; Founder
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* 2. What the visit includes */}
      <section className="bg-linen paper-grain py-16 md:py-28">
        <div className="max-w-6xl mx-auto px-6 md:px-10">
          <Reveal>
            <div className="font-meta text-forest mb-5">What Your Visit Includes</div>
          </Reveal>
          <Reveal delay={100}>
            <h2 className="font-display text-3xl md:text-4xl lg:text-5xl leading-[1.12] tracking-tight text-umber max-w-2xl">
              A complete picture, in one{" "}
              <span className="font-display-italic">appointment.</span>
            </h2>
          </Reveal>

          <div className="mt-12 border-t border-umber/20">
            {INCLUDED.map((item, i) => (
              <Reveal key={item.name} delay={80 + i * 60}>
                <div
                  data-testid={`np-included-${i}`}
                  className="grid grid-cols-12 gap-5 md:gap-8 border-b border-umber/20 py-7 md:py-9"
                >
                  <div className="col-span-12 md:col-span-5">
                    <h3 className="font-display text-xl md:text-2xl text-umber leading-snug">
                      {item.name}
                    </h3>
                  </div>
                  <div className="col-span-12 md:col-span-7 text-umber/85 leading-relaxed text-base">
                    {item.body}
                  </div>
                </div>
              </Reveal>
            ))}
          </div>

          <Reveal delay={200}>
            <div
              data-testid="np-fees"
              className="mt-12 bg-paper border border-umber/15 p-7 md:p-10 max-w-3xl"
            >
              <div className="font-meta text-forest mb-4">Fees &amp; The Gift</div>
              <p className="text-base leading-relaxed text-umber/85">
                Your New Patient Experience is billed at regular fees, and may be
                covered in full or in part by your dental insurance. Your Philips
                Sonicare is yours to take home at the end of your first visit.
              </p>
              <p className="mt-4 text-sm leading-relaxed text-umber/65">
                One gift per new patient, while quantities last. Treatment
                recommendations are always based on an individual clinical
                assessment. All dental services and treatments at Alae Dental are
                provided by General Dentists.
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* 3. Meet Dr. Mirza */}
      <section data-testid="np-meet-dr-mirza" className="bg-paper paper-grain py-16 md:py-28 border-y border-umber/10">
        <div className="max-w-4xl mx-auto px-6 md:px-10">
          <Reveal>
            <div className="font-meta text-forest mb-5">Meet Your Dentist</div>
          </Reveal>
          <Reveal delay={100}>
            <h2 className="font-display text-3xl md:text-4xl lg:text-5xl leading-[1.12] tracking-tight text-umber">
              Dr. Nausheen{" "}
              <span className="font-display-italic">Mirza.</span>
            </h2>
          </Reveal>
          <Reveal delay={150}>
            <div className="mt-8 space-y-5 text-base md:text-lg leading-relaxed text-umber/85">
              <p>
                Dr. Nausheen Mirza is a Toronto-born General Dentist with over 13
                years of clinical experience across Canada and the United States.
                Raised in Woodbridge from the age of two, she returned home to
                bring her vision for Alae Dental to life and serve the community
                she has always called home.
              </p>
              <p>
                She earned her Doctor of Dental Surgery from New York University
                College of Dentistry, completed an Implant Fellowship through the
                University of Florida, and has pursued continuing education
                through Spear Education and the Las Vegas Institute. She also
                served for many years as a Clinical Instructor at the University
                of Toronto Faculty of Dentistry.
              </p>
              <p>
                Known for her calm demeanor, gentle approach, and attention to
                detail, Dr. Mirza believes dental care should never feel rushed or
                impersonal. She takes the time to understand each patient's needs
                and goals, explain their options, and help them feel comfortable
                and informed throughout their care.
              </p>
            </div>
          </Reveal>
          <Reveal delay={200}>
            <p className="mt-10 font-display-italic text-xl md:text-2xl leading-snug text-umber border-l-2 border-forest/40 pl-6">
              &ldquo;After years of practicing and teaching, I knew exactly what I
              wanted to create. A practice where I could deliver dentistry the way
              I believe it should be practiced, with the care and attention every
              patient deserves.&rdquo;
            </p>
          </Reveal>
        </div>
      </section>

      {/* 4. Enquiry form */}
      <section id="callback" className="bg-linen paper-grain py-16 md:py-28 scroll-mt-20">
        <div className="max-w-3xl mx-auto px-6 md:px-10">
          <Reveal>
            <div className="font-meta text-forest mb-5">Request a Callback</div>
          </Reveal>
          <Reveal delay={100}>
            <h2 className="font-display text-3xl md:text-4xl leading-[1.12] tracking-tight text-umber">
              Leave your details and we'll{" "}
              <span className="font-display-italic">call you.</span>
            </h2>
          </Reveal>
          <Reveal delay={150}>
            <p className="mt-6 text-base leading-relaxed text-umber/85">
              Tell us how to reach you and we'll arrange your New Patient
              Experience at a time that suits you. No need to choose a service,
              we know why you're here.
            </p>
          </Reveal>
          <Reveal delay={200}>
            <div className="mt-10 bg-paper border border-umber/15 p-7 md:p-10 shadow-paper">
              <CallbackForm offer={OFFER_ID} offerLabel={OFFER_LABEL} />
            </div>
          </Reveal>
        </div>
      </section>

      {/* 6. Compact footer: address, call, book */}
      <footer className="bg-umber text-linen paper-grain">
        <div className="max-w-6xl mx-auto px-6 md:px-10 py-12 md:py-16 grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-10">
          <div>
            <Logo size="sm" invert className="h-9" />
            <p className="mt-5 text-sm leading-relaxed text-linen/70">
              Family and cosmetic dentistry in Vaughan.
            </p>
          </div>
          <div className="text-sm leading-relaxed text-linen/80">
            <div className="font-meta text-linen/50 mb-3">Visit</div>
            <a
              href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
                CONTACT.mapQuery
              )}`}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="np-map"
              className="inline-flex items-start gap-2 hover:text-linen transition-colors"
            >
              <MapPin size={15} className="mt-0.5 shrink-0" strokeWidth={1.7} />
              <span>
                {CONTACT.addressLines.map((line) => (
                  <span key={line} className="block">
                    {line}
                  </span>
                ))}
              </span>
            </a>
          </div>
          <div className="text-sm leading-relaxed text-linen/80">
            <div className="font-meta text-linen/50 mb-3">Contact</div>
            <a
              href={`tel:${CONTACT.phoneTel}`}
              data-testid="np-call-footer"
              onClick={() => trackEvent("call_click", { offer: OFFER_ID, placement: "np-footer" })}
              className="inline-flex items-center gap-2 hover:text-linen transition-colors py-2 min-h-[44px]"
            >
              <Phone size={15} strokeWidth={1.7} />
              {CONTACT.phoneLabel}
            </a>
            <div className="mt-2">
              <a
                href="#callback"
                data-testid="np-request-callback-footer"
                className="inline-flex items-center gap-2 text-[11px] tracking-[0.2em] uppercase underline underline-offset-4 decoration-linen/40 hover:decoration-linen py-2 min-h-[44px]"
              >
                Request a Callback
              </a>
            </div>
          </div>
        </div>
        <div className="border-t border-linen/15">
          <div className="max-w-6xl mx-auto px-6 md:px-10 py-6 text-[11px] text-linen/50 flex flex-wrap gap-x-6 gap-y-2 justify-between">
            <span>© {new Date().getFullYear()} Alae Dental</span>
            <Link to="/privacy" className="hover:text-linen transition-colors">
              Privacy Policy
            </Link>
          </div>
        </div>
      </footer>
    </main>
  );
}
